import { useState } from 'react';
import { List, LayoutGrid, Search, Check } from 'lucide-react';
import type { MetaPage } from '@/pages/Meta/Connection/types/meta.types';

type FilterType = 'pages' | 'category';

interface CompactPageSelectorProps {
    pages: MetaPage[];
    selectedIds: number[];
    onChange: (ids: number[]) => void;
}

/**
 * Seletor de páginas compacto (tabs Páginas/Categoria + busca + lista com
 * avatar pequeno), no mesmo padrão visual do AddPagesPopover usado no Flow —
 * mais sutil e denso que o card grande do FunnelPageSelector.
 */
export default function CompactPageSelector({ pages, selectedIds, onChange }: CompactPageSelectorProps) {
    const [filterType, setFilterType] = useState<FilterType>('pages');
    const [search, setSearch] = useState('');
    const [activeCategories, setActiveCategories] = useState<Set<number>>(new Set());

    const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

    const categories = Array.from(
        new Map(
            pages
                .filter((p) => p.internal_category_id && p.internal_category_name)
                .map((p) => [p.internal_category_id as number, p.internal_category_name as string])
        ).entries()
    ).map(([id, name]) => ({ id, name }));

    const filtered = pages.filter((p) => {
        if (filterType === 'category' && activeCategories.size > 0) {
            return activeCategories.has(p.internal_category_id as number);
        }
        if (filterType === 'pages' && search.trim()) {
            const t = normalize(search);
            return normalize(p.page_name).includes(t) || p.page_id.includes(search.trim());
        }
        return true;
    });

    const toggleCategory = (id: number) => {
        setActiveCategories((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggle = (id: number) => {
        onChange(selectedIds.includes(id) ? selectedIds.filter((i) => i !== id) : [...selectedIds, id]);
    };

    const selectAllFiltered = () => {
        const ids = new Set(selectedIds);
        filtered.forEach((p) => ids.add(p.id));
        onChange(Array.from(ids));
    };

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            {/* Tabs */}
            <div className="flex border-b border-gray-100">
                <button
                    type="button"
                    onClick={() => setFilterType('pages')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors ${
                        filterType === 'pages'
                            ? 'text-gray-900 border-b-2 border-gray-800'
                            : 'text-gray-400 hover:text-gray-600'
                    }`}
                >
                    <List className="w-3.5 h-3.5" />
                    Páginas
                </button>
                <button
                    type="button"
                    onClick={() => setFilterType('category')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors ${
                        filterType === 'category'
                            ? 'text-gray-900 border-b-2 border-gray-800'
                            : 'text-gray-400 hover:text-gray-600'
                    }`}
                >
                    <LayoutGrid className="w-3.5 h-3.5" />
                    Categoria
                </button>
            </div>

            {/* Busca (modo páginas) */}
            {filterType === 'pages' && (
                <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar página..."
                            className="w-full h-8 pl-8 pr-3 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300"
                        />
                    </div>
                </div>
            )}

            {/* Categoria chips */}
            {filterType === 'category' && (
                <div className="p-2 border-b border-gray-100">
                    {categories.length === 0 ? (
                        <p className="text-xs text-gray-400 px-1 py-1">Nenhuma categoria cadastrada</p>
                    ) : (
                        <div className="flex flex-wrap gap-1.5">
                            {categories.map((c) => (
                                <button
                                    type="button"
                                    key={c.id}
                                    onClick={() => toggleCategory(c.id)}
                                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                        activeCategories.has(c.id)
                                            ? 'bg-gray-800 text-white border-gray-800'
                                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                                    }`}
                                >
                                    {c.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Selecionar todas (filtradas) */}
            {filtered.length > 0 && (
                <div className="px-3 py-1.5 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-[11px] text-gray-400">
                        {selectedIds.length} selecionada{selectedIds.length !== 1 ? 's' : ''}
                    </span>
                    <button
                        type="button"
                        onClick={selectAllFiltered}
                        className="text-[11px] font-medium text-cyan-600 hover:text-cyan-700"
                    >
                        Selecionar {filtered.length}
                    </button>
                </div>
            )}

            {/* Lista */}
            <div className="max-h-52 overflow-y-auto">
                {filtered.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-6">Nenhuma página encontrada</p>
                ) : (
                    filtered.map((page) => {
                        const selected = selectedIds.includes(page.id);
                        return (
                            <button
                                type="button"
                                key={page.id}
                                onClick={() => toggle(page.id)}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition-colors text-left ${
                                    selected ? 'bg-cyan-50/60' : ''
                                }`}
                            >
                                <div
                                    className={`w-4 h-4 rounded flex items-center justify-center shrink-0 border-2 transition-colors ${
                                        selected ? 'bg-cyan-500 border-cyan-500' : 'border-gray-300 bg-white'
                                    }`}
                                >
                                    {selected && <Check className="h-2.5 w-2.5 text-white" />}
                                </div>
                                <div className="w-7 h-7 bg-gray-800 rounded-full flex items-center justify-center shrink-0">
                                    <span className="text-white font-semibold text-[10px]">
                                        {page.page_name.substring(0, 2).toUpperCase()}
                                    </span>
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs font-medium text-gray-800 truncate">{page.page_name}</p>
                                    <p className="text-[10px] text-gray-400">Conta: {page.meta_user_name ?? '-'}</p>
                                </div>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
}
